import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Truck } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";

export default function Shop() {
  const { L, products } = useStore();
  const [params, setParams] = useSearchParams();
  const collection = params.get("collection") || "all";
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("featured");

  const filters = [
    { key: "all", label: L.shop.all },
    { key: "classiques", label: L.nav.classics },
    { key: "melanges", label: L.nav.blended },
    { key: "fruite", label: L.nav.fruity },
  ];

  const list = useMemo(() => {
    let out = collection === "all" ? [...products] : products.filter((p) => p.collection === collection);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter((p) => (p.nameFr + " " + p.nameEn).toLowerCase().includes(q));
    }
    if (sort === "priceAsc") out.sort((a, b) => a.price - b.price);
    else if (sort === "priceDesc") out.sort((a, b) => b.price - a.price);
    else if (sort === "alpha") out.sort((a, b) => a.nameEn.localeCompare(b.nameEn));
    else out.sort((a, b) => Number(b.featured) - Number(a.featured));
    return out;
  }, [products, collection, query, sort]);

  const setCollection = (key) => {
    if (key === "all") setParams({});
    else setParams({ collection: key });
  };

  return (
    <div className="pt-32 pb-24 star-lattice min-h-screen" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><p className="eyebrow">{L.shop.eyebrow}</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ivory mt-4">{L.shop.title}</h1></Reveal>
          <Reveal delay={0.14}><p className="text-sand mt-4 text-sm lg:text-base">{L.shop.sub}</p></Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="flex items-center justify-center gap-2 mt-8 p-3 rounded-2xl border border-gold/25 bg-gold/5 max-w-3xl mx-auto" data-testid="shop-free-shipping-banner">
            <Truck className="w-4 h-4 text-gold shrink-0" strokeWidth={1.5} />
            <p className="text-[0.8rem] text-gold/95 leading-snug text-center">{L.shop.freeShip}</p>
          </div>
        </Reveal>

        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mt-12" data-testid="shop-toolbar">
          <div className="flex flex-wrap gap-2" data-testid="shop-filters">
            {filters.map((f) => (
              <button
                key={f.key}
                data-testid={"filter-" + f.key}
                onClick={() => setCollection(f.key)}
                className={
                  "px-5 py-2.5 rounded-full text-[0.65rem] tracking-[0.22em] uppercase transition-all duration-300 border " +
                  (collection === f.key
                    ? "bg-gold text-obsidian border-gold font-semibold"
                    : "border-gold/25 text-ivory/75 hover:border-gold/70 hover:text-gold")
                }
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-1 gap-3 lg:justify-end">
            <div className="relative flex-1 lg:max-w-xs">
              <Search className="w-4 h-4 text-ivory/40 absolute left-4 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
              <input
                data-testid="shop-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={L.shop.searchPlaceholder}
                className="w-full bg-onyx/70 border border-gold/25 rounded-full pl-11 pr-4 py-2.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70 transition-colors"
              />
            </div>
            <select
              data-testid="shop-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-onyx/70 border border-gold/25 rounded-full px-4 py-2.5 text-sm text-ivory focus:border-gold/70 transition-colors"
            >
              <option value="featured">{L.shop.sortFeatured}</option>
              <option value="priceAsc">{L.shop.sortPriceAsc}</option>
              <option value="priceDesc">{L.shop.sortPriceDesc}</option>
              <option value="alpha">{L.shop.sortAlpha}</option>
            </select>
          </div>
        </div>

        <p className="text-[0.65rem] tracking-[0.25em] uppercase text-sand mt-8" data-testid="shop-count">
          {list.length} {L.shop.count}
        </p>

        {list.length === 0 ? (
          <p className="text-center text-sand py-24" data-testid="shop-empty">{L.shop.noResults}</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-9 mt-6">
            {list.map((p, i) => (
              <ProductCard key={p.slug} p={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
